"use client";

import { useLayoutEffect, useRef, useState } from "react";

export type DocsHeading = {
  id: string;
  title: string;
  depth: number;
  method?: string;
};

export function ContentsNavigation({
  headings,
  activeHeading,
  onSelect,
}: {
  headings: DocsHeading[];
  activeHeading: string;
  onSelect: (id: string) => void;
}) {
  const viewport = useRef<HTMLElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const list = container.current;
    if (!list) return;
    const update = () => {
      const active = list.querySelector<HTMLAnchorElement>(
        '[aria-current="location"]',
      );
      if (!active) {
        setIndicator(null);
        return;
      }
      const panel = viewport.current;
      if (panel && panel.clientHeight > 0) {
        const bounds = panel.getBoundingClientRect();
        const item = active.getBoundingClientRect();
        const padding = 16;
        const delta =
          item.top < bounds.top + padding
            ? item.top - bounds.top - padding
            : item.bottom > bounds.bottom - padding
              ? item.bottom - bounds.bottom + padding
              : 0;
        if (delta)
          panel.scrollTo({
            top: panel.scrollTop + delta,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "instant"
              : "smooth",
          });
      }
      const next = {
        top: active.offsetTop,
        left: active.offsetLeft,
        width: active.offsetWidth,
        height: active.offsetHeight,
      };
      setIndicator((previous) =>
        previous &&
        Object.entries(next).every(
          ([key, value]) => previous[key as keyof typeof next] === value,
        )
          ? previous
          : next,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(list);
    list.querySelectorAll("a").forEach((link) => observer.observe(link));
    return () => observer.disconnect();
  }, [headings, activeHeading]);

  return (
    <nav
      ref={viewport}
      className="lb-scrollbar-overlay min-h-0 flex-1 px-3 py-4 text-sm"
    >
      <div ref={container} className="relative">
        {indicator && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 rounded-md bg-sidebar-accent transition-[transform,width,height] duration-200 ease-in-out motion-reduce:transition-none"
            style={{
              width: indicator.width,
              height: indicator.height,
              transform: `translate(${indicator.left}px, ${indicator.top}px)`,
            }}
          />
        )}
        {headings.map((heading) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            aria-current={activeHeading === heading.id ? "location" : undefined}
            onClick={() => onSelect(heading.id)}
            className={`relative block break-words rounded-md px-3 py-1.5 font-medium transition-colors hover:text-gray-1100 motion-reduce:transition-none dark:hover:text-gray-100 ${activeHeading === heading.id ? "text-gray-1100 dark:text-gray-100" : `hover:bg-sidebar-accent ${heading.depth > 2 ? "text-muted" : "text-foreground"}`} ${heading.depth > 2 ? "ml-3" : "mb-1"}`}
          >
            <span
              className={heading.method ? "flex items-start gap-2" : undefined}
            >
              {heading.method && (
                <span
                  className={`api-method api-method-${heading.method.toLowerCase()} api-contents-method`}
                >
                  {heading.method}
                </span>
              )}
              <span className="min-w-0">
                {heading.title
                  .split(/(?<=[a-z])(?=[A-Z])/)
                  .map((part, index) => (
                    <span key={index}>
                      {index > 0 && <wbr />}
                      {part}
                    </span>
                  ))}
              </span>
            </span>
          </a>
        ))}
      </div>
    </nav>
  );
}
